import type { BookingConversionIntent } from './events'
import { isBrowserAdvertisingMeasurementGranted } from '@/lib/consent/advertising-consent'

type OaiqWindow = Window & {
  oaiq?: (...args: unknown[]) => void
}

/**
 * Booking intents accepted by /api/book. Mirrors the `intent` enum in
 * `lib/intake/schema.ts`; kept as a local type so this module stays free of
 * server-only imports and can be pulled into both the browser bundle and the
 * route handler.
 */
export type BookingIntent = BookingConversionIntent

/** The subset of OpenAI's standard events this site emits. */
export type OpenAiEventName = 'page_viewed' | 'appointment_scheduled' | 'lead_created'

/**
 * OpenAI groups every standard event under a `data.type` bucket. Sending the
 * wrong bucket for an event name is a validation failure server-side and a
 * silently mis-bucketed conversion browser-side, so the pairing is derived
 * here rather than passed in by callers.
 */
const EVENT_DATA_TYPE: Record<OpenAiEventName, 'contents' | 'customer_action'> = {
  page_viewed: 'contents',
  appointment_scheduled: 'customer_action',
  lead_created: 'customer_action',
}

export function openAiEventDataType(name: OpenAiEventName): 'contents' | 'customer_action' {
  return EVENT_DATA_TYPE[name]
}

/**
 * Map a booking intent to the OpenAI standard event it converts as.
 *
 * `assessment` and `online_quote` both put a real date + time on the calendar,
 * which is what `appointment_scheduled` means. `callback_lead` — the short LP
 * form — books nothing; it is a request to be phoned, so it converts as
 * `lead_created`. Both live under the `customer_action` data type.
 *
 * IMPORTANT: this function is imported by BOTH the browser pixel callers and
 * the server-side Conversions API fire in /api/book. Deduplication matches on
 * (pixel ID, event name, event ID) — if the two sides ever disagree about the
 * event name for an intent, OpenAI stops collapsing the pair and every booking
 * counts twice. Change this in one place only.
 */
export function openAiConversionEvent(intent: BookingIntent): OpenAiEventName {
  return intent === 'callback_lead' ? 'lead_created' : 'appointment_scheduled'
}

/**
 * Fire an OpenAI Ads pixel event.
 *
 * No-ops when the pixel never loaded. The install snippet is env-gated on
 * `OPEN_AI_PIXEL` (Production only — Preview is intentionally blank so preview
 * traffic stays out of ad reporting), so on Preview, local dev, and during SSR
 * `window.oaiq` is simply undefined. Callers don't branch on whether the pixel
 * is configured.
 *
 * `eventId` is OpenAI's deduplication key, passed in the 4th argument as
 * `event_id`. The server-side Conversions API sends the same value as the
 * event's `id` field, which is how the browser and server fires collapse into
 * one conversion instead of two.
 *
 * NEVER pass customer PII (name, email, phone, address). OpenAI's identity
 * matching requires SHA-256 hashed values; raw identifiers must not reach
 * `oaiq`. Server-side hashing lives in `lib/analytics/openai-capi.ts`.
 */
export function trackOpenAiEvent(
  name: OpenAiEventName,
  options: { eventId?: string } = {},
): void {
  if (!isBrowserAdvertisingMeasurementGranted()) return
  const oaiq = (window as OaiqWindow).oaiq
  if (typeof oaiq !== 'function') return

  const data = { type: openAiEventDataType(name) }
  // Only pass the 4th argument when there is an ID to send — an options object
  // carrying an undefined event_id is a malformed dedup key.
  if (options.eventId) {
    oaiq('measure', name, data, { event_id: options.eventId })
    return
  }
  oaiq('measure', name, data)
}

/**
 * A booking or callback form was submitted successfully.
 *
 * This is the fire that lets OpenAI Ads delivery optimise toward completed
 * bookings instead of cheap landing-page clicks. The booking reference code
 * doubles as the dedup key — it is an opaque internal ID, not PII, and the
 * server sends the identical value from /api/book.
 */
export function trackOpenAiConversion(input: {
  referenceCode: string
  intent: BookingIntent
}): void {
  trackOpenAiEvent(openAiConversionEvent(input.intent), { eventId: input.referenceCode })
}
